import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { FAQItem } from '@/config/content';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQProps {
  faqs: FAQItem[];
  sectionTitle?: string;
}

const FAQAccordion: React.FC<{ faq: FAQItem; isOpen: boolean; onToggle: () => void }> = ({
  faq, isOpen, onToggle,
}) => (
  <div className="surface overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/50 transition-colors"
    >
      <h3 className="font-heading font-semibold text-foreground pr-4 text-sm sm:text-base">
        {faq.question}
      </h3>
      <ChevronDown
        size={20}
        className={`text-primary flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="px-5 pb-5 text-muted-foreground text-sm leading-relaxed">
            {faq.answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const FAQ: React.FC<FAQProps> = ({ faqs, sectionTitle = 'Frequently Asked Questions' }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-20 lg:py-28">
      <div className="container mx-auto px-4 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
            {sectionTitle}
          </h2>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <FAQAccordion
                faq={faq}
                isOpen={openIndex === index}
                onToggle={() => setOpenIndex(openIndex === index ? null : index)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
